/**
 * @file Maintenance board adapter.
 */

import {
  collection,
  getDocs,
  query,
  where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../../app/firebase/firebase.js";
import {
  appData,
  saveViewSettings,
  state,
} from "../../app/state/state.js";
import { createParentBoardAdapter } from "../../shared/board/parentBoardAdapterFactory.js";
import {
  renderBoardCardChecklistPreview,
  renderBoardCardDateSlot,
  renderBoardCardInfoRow,
  renderBoardCardShell,
  renderBoardCardStatusChip,
  renderBoardCardTypeChip,
  renderBoardCardUserSlot,
  formatBoardCardDate,
  formatBoardCardDateCompact,
} from "../../shared/board/boardCardView.js";
import {
  renderGroupedKanbanBoard,
  renderParentWIPKanbanBoard,
} from "../../shared/board/boardKanbanView.js";
import { applyBoardRuntimePipeline } from "../../shared/board/boardRuntime.js";
import {
  getResolvedCardDate,
  sortCanonicalTaskItems,
} from "../../shared/board/itemSortUtils.js";
import { getBoardTableColumnDefinitions } from "../../shared/board/boardTableColumns.js";
import {
  renderBoardTableHeader,
  renderBoardTableStateCard,
  renderGroupedTableSections,
} from "../../shared/board/boardTableView.js";
import { addBoardTableInteractionListeners } from "../../shared/board/boardTableInteractions.js";
import {
  renderBoardTableTypeBadges,
  renderQuickAddTaskCell,
} from "../../shared/board/boardTableCells.js";
import { renderTaskListHtml } from "../../shared/board/parentBoardCards.js";
import { isItemClosed } from "../../shared/utils/closedState.js";
import {
  formatCurrency,
  isColorDark,
} from "../../shared/utils/displayUtils.js";
import { formatDate } from "../../shared/utils/dateUtils.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
  getUserDisplayNameById,
  getUserFullDisplayName,
  getUserInitials,
} from "../../shared/utils/userDisplay.js";

const MAINTENANCE_ITEM_TYPE = "maintenance";
const MAINTENANCE_VIEW_KEY = "maintenance";
const WIP_CHECKLIST_LIMIT = 9999;
const STREAM_META = Object.freeze({
  service: Object.freeze({
    label: "Service",
    icon: "build_circle",
    color: "#4599e8",
    fillColor: "#e4f2ff",
    classes: "border-sky-100 bg-sky-50 text-sky-700",
  }),
  issue: Object.freeze({
    label: "Issue",
    icon: "report",
    color: "#d65f5f",
    fillColor: "#fde3e3",
    classes: "border-rose-100 bg-rose-50 text-rose-600",
  }),
});

function getMaintenanceItems() {
  return appData?.maintenance || [];
}

function getMaintenanceTypesConfig() {
  return appData?.config?.maintenanceTypes || [];
}

function getMaintenanceStatusConfig(item) {
  return (appData?.config?.maintenanceStatus || [])
    .find((status) => status.id === item?.status) || {};
}

function getPriorityConfig(item) {
  return (appData?.config?.priorities || [])
    .find((priority) => priority.id === item?.priority) || {};
}

function getStreamMeta(stream) {
  return STREAM_META[stream] || Object.freeze({
    label: stream ? String(stream) : "Stream",
    icon: "construction",
    color: "#64748b",
    classes: "border-slate-200 bg-slate-50 text-slate-600",
  });
}

function renderBadge(label, color, fallbackClasses = "") {
  const safeLabel = escapeHTML(String(label || ""));
  if (!safeLabel) {return "";}

  if (!color) {
    return `<span class="inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${fallbackClasses}">${safeLabel}</span>`;
  }

  const textColor = isColorDark(color) ? "#ffffff" : "#111827";
  return `<span class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium" style="background-color: ${color}; color: ${textColor};">${safeLabel}</span>`;
}

function renderStreamBadge(stream) {
  const meta = getStreamMeta(stream);
  return `
    <span class="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-normal ${meta.classes}" title="${escapeHTML(meta.label)}">
      ${renderStreamIcon(stream, {className: "text-[13px]"})}
      ${escapeHTML(meta.label)}
    </span>
  `;
}

function renderStreamIcon(stream, {className = ""} = {}) {
  const meta = getStreamMeta(stream);
  return `
    <span class="maintenance-stream-icon-colored shrink-0 text-[22px] leading-none ${className}" style="--maintenance-stream-outline: ${meta.color}; --maintenance-stream-fill: ${meta.fillColor || "transparent"};" title="${escapeHTML(meta.label)}" aria-label="${escapeHTML(meta.label)}">
      <span class="maintenance-stream-icon-colored__fill material-symbols-outlined" aria-hidden="true">${meta.icon}</span>
      <span class="maintenance-stream-icon-colored__outline material-symbols-outlined">${meta.icon}</span>
    </span>
  `;
}

function getLinkedEquipmentName(item) {
  if (!item?.linked_asset_id) {return "";}
  const asset = (appData?.assets || [])
    .find((entry) => entry.id === item.linked_asset_id);
  return asset?.name ||
    item.linked_asset_name ||
    item.linked_asset_id ||
    "";
}

function renderPriorityChip(item) {
  const priority = getPriorityConfig(item);
  return renderBadge(priority?.name || item?.priority || "N/A", priority?.color || "#e5e7eb");
}

function getManagedByDisplay(item) {
  const userId = item?.managed_by || "";
  if (!userId) {
    return {
      text: "",
      title: "No manager",
      name: "",
    };
  }

  const user = (appData?.users || []).find((entry) => entry.id === userId);
  return {
    text: getUserInitials(userId) || "??",
    title: getUserFullDisplayName(user) || getUserDisplayNameById(userId) || "Unknown manager",
    name: getUserDisplayNameById(userId) || "",
  };
}

function getMaintenanceDateTooltip(item, resolvedDate) {
  if (!resolvedDate) {return "";}
  if (isItemClosed(item)) {
    return `Completed: ${formatDate(resolvedDate)}`;
  }
  return `Due: ${formatDate(resolvedDate)}`;
}

function renderQuickAddTaskButton(item) {
  if (!item?.id || isItemClosed(item)) {return "";}
  return `
    <button type="button" class="quick-add-task-btn icon-action-btn kanban-card-action-btn absolute top-2 right-2 opacity-0 group-hover:opacity-100" data-maintenance-id="${item.id}" title="Add Task">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
      </svg>
    </button>
  `;
}

function getOpenMaintenanceTasks(item, taskList = appData?.tasks || []) {
  const tasks = (taskList || [])
    .filter((task) => task?.parentId === item?.id && !isItemClosed(task));
  return sortCanonicalTaskItems(tasks, taskList || []);
}

function renderMaintenanceTaskListHtml(item, {limit = 3, taskList = appData?.tasks || []} = {}) {
  const tasks = getOpenMaintenanceTasks(item, taskList);
  if (tasks.length === 0) {return "";}

  return renderTaskListHtml(tasks, {
    showInitials: true,
    limit,
  });
}

function renderMaintenanceCard(
  item,
  {
    taskLimit = 3,
    checklistLimit = 1,
    taskList = appData?.tasks || [],
  } = {},
) {
  if (!item?.id) {
    return "<div class=\"kanban-card bg-red-100 p-3 rounded-md border-l-4 border-red-500 text-xs\">Invalid Card Data</div>";
  }

  const status = getMaintenanceStatusConfig(item);
  const priority = getPriorityConfig(item);
  const resolvedDate = getResolvedCardDate(MAINTENANCE_ITEM_TYPE, item, taskList);
  const dateText = formatBoardCardDate(resolvedDate);
  const cardTitle = item.item || "Untitled Maintenance";
  const linkedEquipmentName = getLinkedEquipmentName(item);
  const taskListHtml = renderMaintenanceTaskListHtml(item, {
    limit: taskLimit,
    taskList,
  });
  const embeddedContentHtml = taskListHtml ?
    `<div class="space-y-1 pt-1">${taskListHtml}</div>` :
    "";
  const infoRowHtml = renderBoardCardInfoRow({
    statusChipHtml: renderBoardCardStatusChip(status),
    typeChipHtml: renderBoardCardTypeChip(
      Array.isArray(item.types) ? item.types : [],
      getMaintenanceTypesConfig(),
      { singleOnly: true, overflowLabel: "+" },
    ),
    dateHtml: renderBoardCardDateSlot({
      dateText,
      compactDateText: formatBoardCardDateCompact(resolvedDate),
      tooltip: getMaintenanceDateTooltip(item, resolvedDate),
    }),
    userHtml: renderBoardCardUserSlot(getManagedByDisplay(item)),
    showUserSlot: true,
    hasEmbeddedContent: Boolean(embeddedContentHtml),
  });

  return renderBoardCardShell({
    itemId: item.id,
    borderColor: priority?.color || status?.color || "#e5e7eb",
    titleHtml: `
      <p class="mb-1 min-w-0 truncate pr-7 text-sm font-semibold text-gray-900 dark:text-gray-200" title="${escapeHTML(cardTitle)}">
        ${escapeHTML(cardTitle)}
      </p>
      <span class="pointer-events-none absolute right-2 top-2 h-[22px] w-[22px]">
        ${renderStreamIcon(item.stream, {className: "h-[22px] w-[22px]"})}
      </span>
    `,
    secondaryTitleHtml: linkedEquipmentName ?
      `<p class="mb-2 truncate text-xs text-gray-500 dark:text-gray-400" title="${escapeHTML(linkedEquipmentName)}">${escapeHTML(linkedEquipmentName)}</p>` :
      '<div class="mb-2"></div>',
    infoRowHtml,
    embeddedContentHtml,
    checklistHtml: renderBoardCardChecklistPreview(item, MAINTENANCE_ITEM_TYPE, {limit: checklistLimit}),
    actionButtonHtml: renderQuickAddTaskButton(item),
  });
}

async function loadMoreMaintenanceClosedItems(collectionType) {
  const { loadMoreClosedItems } = await import("../../shared/api/parentItems.js");
  await loadMoreClosedItems(collectionType);
}

async function fetchAllOpenTasksForParentType(parentType) {
  const allTasksQuery = query(
    collection(db, "tasks"),
    where("parentType", "==", parentType),
    where("isSubtask", "==", true),
    where("isClosedResolved", "==", false),
  );
  const snapshot = await getDocs(allTasksQuery);
  return snapshot.docs.map((snapshotDoc) => ({
    id: snapshotDoc.id,
    ...snapshotDoc.data(),
  }));
}

function renderMaintenanceKanban(board) {
  const settings = state.viewSettings?.maintenance;
  if (!settings) {
    console.error("renderMaintenanceKanban: Maintenance view settings not loaded.");
    if (board) {
      board.className = "grid grid-cols-1 gap-6";
      board.innerHTML = renderBoardTableStateCard({
        title: "Board unavailable",
        body: "The board settings could not be loaded.",
        tone: "error",
      });
    }
    return;
  }

  const sourceItems = getMaintenanceItems();
  renderGroupedKanbanBoard(board, {
    viewKey: MAINTENANCE_VIEW_KEY,
    itemType: MAINTENANCE_ITEM_TYPE,
    singularLabel: "maintenance item",
    sourceItems,
    items: applyBoardRuntimePipeline({
      viewKey: MAINTENANCE_VIEW_KEY,
      itemType: MAINTENANCE_ITEM_TYPE,
      items: sourceItems,
      taskList: appData.tasks,
    }),
    groupBy: settings.boardGroupBy,
    dateGroupingMode: settings.boardDateGroupingMode,
    taskList: appData.tasks,
    renderCard: renderMaintenanceCard,
    onLoadMoreClosed: loadMoreMaintenanceClosedItems,
  });
}

async function openMaintenanceDetails(maintenanceId) {
  const { showDetails } = await import("../../app/routing/navigationController.js");
  showDetails(maintenanceId, MAINTENANCE_ITEM_TYPE);
}

function renderMaintenanceWipKanban(board) {
  renderParentWIPKanbanBoard(board, {
    viewKey: MAINTENANCE_VIEW_KEY,
    itemType: MAINTENANCE_ITEM_TYPE,
    parentType: MAINTENANCE_ITEM_TYPE,
    items: getMaintenanceItems(),
    renderCard: (item, openTasks) => renderMaintenanceCard(item, {
      taskLimit: null,
      checklistLimit: WIP_CHECKLIST_LIMIT,
      taskList: openTasks,
    }),
    quickAddDatasetKey: "maintenanceId",
    fetchOpenTasksForParentType: fetchAllOpenTasksForParentType,
    onQuickAddTask: (parentId) => openMaintenanceTaskForm({
      parentId,
      owner: state.currentUser?.uid,
    }),
    onOpenDetails: openMaintenanceDetails,
    onLoadMoreClosed: loadMoreMaintenanceClosedItems,
  });
}

function renderMaintenanceTableCell(item, columnKey) {
  switch (columnKey) {
    case "item": {
      const title = item.item || "Untitled Maintenance";
      return `
        <span class="flex min-w-0 items-center gap-1.5">
          ${renderStreamIcon(item.stream, {className: "text-[20px]"})}
          <span class="min-w-0 truncate font-medium text-gray-800">${escapeHTML(title)}</span>
        </span>
      `;
    }
    case "linked_to":
      return getLinkedEquipmentName(item) ?
        escapeHTML(getLinkedEquipmentName(item)) :
        "<span class=\"text-xs text-gray-400\">None</span>";
    case "stream":
      return renderStreamBadge(item.stream);
    case "types":
      return renderBoardTableTypeBadges(
        Array.isArray(item.types) ? item.types : [],
        getMaintenanceTypesConfig(),
      );
    case "priority":
      return renderPriorityChip(item);
    case "status":
      return renderBoardCardStatusChip(getMaintenanceStatusConfig(item));
    case "managed_by":
      return escapeHTML(getManagedByDisplay(item).name || "");
    case "technician":
      return item.technician ?
        escapeHTML(String(item.technician)) :
        "<span class=\"text-xs text-gray-400\">Unassigned</span>";
    case "due_date":
    case "completed_on":
      return escapeHTML(formatDate(item[columnKey]) || "");
    case "cost":
      return escapeHTML(formatCurrency(item.cost) || "");
    default:
      return escapeHTML(String(item[columnKey] ?? ""));
  }
}

function stripHtml(html) {
  const element = document.createElement("div");
  element.innerHTML = html;
  return element.textContent || "";
}

function getMaintenanceVisibleColumns(settings = {}) {
  const configuredColumns = Array.isArray(settings.visibleColumns) ?
    settings.visibleColumns :
    [];
  return configuredColumns.filter((column) => column !== "stream");
}

function renderMaintenanceSingleTable(items, itemType, activeFilterKeys = []) {
  const settings = state.viewSettings?.maintenance;
  if (!settings) {
    console.error("renderMaintenanceSingleTable: Settings not found for maintenance.");
    return "<p class=\"p-4 text-red-500\">Error: Table settings missing.</p>";
  }

  const visibleColumns = getMaintenanceVisibleColumns(settings);
  const showTaskQuickClose = false;
  const tableHeaderHtml = renderBoardTableHeader({
    visibleColumns,
    columnLabels: getBoardTableColumnDefinitions(MAINTENANCE_VIEW_KEY),
    columnWidths: settings.columnWidths || {},
    itemType,
    showTaskQuickClose,
  });

  const tableBodyHtml = items.length > 0 ?
    `<tbody>
      ${items.map((item) => {
        const quickAddHtml = !isItemClosed(item) ?
          renderQuickAddTaskCell(MAINTENANCE_ITEM_TYPE, item.id) :
          "";

        return `<tr data-id="${item.id}" class="cursor-default border-b bg-white hover:bg-gray-50">
          ${visibleColumns.map((columnKey) => {
            const content = renderMaintenanceTableCell(item, columnKey);
            const titleText = stripHtml(content);
            const titleAttr = titleText.length > 50 ? ` title="${escapeHTML(titleText)}"` : "";
            const cellClass = columnKey === "types" ?
              "px-6 py-4 align-top" :
              "px-6 py-4";
            return `<td class="${cellClass}"${titleAttr}>${content}</td>`;
          }).join("")}
          ${quickAddHtml}
        </tr>`;
      }).join("")}
    </tbody>` :
    `<tbody>
      <tr>
        <td colspan="${visibleColumns.length + 1}" class="py-8 text-center text-gray-500">
          No maintenance items found${activeFilterKeys?.length > 0 ? " matching filters" : ""}.
        </td>
      </tr>
    </tbody>`;

  return `<table class="w-full min-w-[920px] table-fixed text-left text-sm text-gray-500 md:min-w-0">
    ${tableHeaderHtml}
    ${tableBodyHtml}
  </table>`;
}

async function openMaintenanceTaskForm(item) {
  const { openParentForm } = await import("../../shared/forms/parentFormModal.js");
  openParentForm("task", item);
}

function renderMaintenanceTable(board) {
  if (!board) {
    console.error("renderMaintenanceTable: Board element not provided.");
    return;
  }

  const settings = state.viewSettings?.maintenance;
  if (!settings) {
    console.error("renderMaintenanceTable: Maintenance view settings not loaded.");
    board.className = "";
    board.innerHTML = renderBoardTableStateCard({
      title: "Board unavailable",
      body: "The table settings could not be loaded.",
      tone: "error",
    });
    return;
  }

  const sourceItems = getMaintenanceItems();
  const filteredItems = applyBoardRuntimePipeline({
    viewKey: MAINTENANCE_VIEW_KEY,
    itemType: MAINTENANCE_ITEM_TYPE,
    items: sourceItems,
    taskList: appData.tasks,
  });

  board.className = "";
  board.innerHTML = renderGroupedTableSections(filteredItems, {
    viewKey: MAINTENANCE_VIEW_KEY,
    itemType: MAINTENANCE_ITEM_TYPE,
    singularLabel: "maintenance item",
    sourceItems,
    groupBy: settings.tableGroupBy,
    dateGroupingMode: settings.tableDateGroupingMode,
    activeFilterKeys: [],
    renderTable: renderMaintenanceSingleTable,
  });

  addBoardTableInteractionListeners(board, {
    viewType: MAINTENANCE_ITEM_TYPE,
    viewKey: MAINTENANCE_VIEW_KEY,
    saveViewSettings,
    getTaskItems: () => appData.tasks || [],
    renderTableForView: () => renderMaintenanceTable(board),
    openTaskForm: openMaintenanceTaskForm,
    openDetails: openMaintenanceDetails,
    quickCloseTask: async () => {},
    loadMoreClosedItems: loadMoreMaintenanceClosedItems,
  });
}

export const maintenanceBoardAdapter = createParentBoardAdapter({
  adapterKey: MAINTENANCE_VIEW_KEY,
  viewKey: MAINTENANCE_VIEW_KEY,
  cards: Object.freeze({
    renderCard: renderMaintenanceCard,
  }),
  tableRows: Object.freeze({
    renderCell: renderMaintenanceTableCell,
  }),
  renderers: Object.freeze({
    table: renderMaintenanceTable,
    kanban: renderMaintenanceKanban,
    wipKanban: renderMaintenanceWipKanban,
  }),
});
